/**
 * useProCalculatorState Hook
 *
 * Centralized state management for ProCalculator using useReducer
 * Replaces multiple useState calls with a single state management pattern
 */

import { useReducer } from 'react';
import type { ProCalculatorState, ProCalculatorAction } from '../types/proCalculator.types';
import type { ProCalculatorFormValues, HandlingFeeType, TariffType } from '../../../types/proCalculator';
import { createDefaultMonthlyConfig } from '../../../types/proCalculator';
import { generateMockBasicValues } from '../../../utils/proCalculator';
import {
  DEFAULT_MONTHLY_CONSUMPTION,
  DEFAULT_CALCULATION_YEAR,
  DEFAULT_DISTRIBUTOR,
  DEFAULT_HANDLING_FEE_TYPE,
  DEFAULT_BASELINE_TARIFF,
} from '../constants/proCalculator.constants';

/**
 * Initial state factory
 * Creates the initial state with default values
 */
export function createInitialState(): ProCalculatorState {
  // Create initial months with mock values
  const months = Array.from({ length: 12 }, () => {
    const config = createDefaultMonthlyConfig();
    config.consumption = DEFAULT_MONTHLY_CONSUMPTION;
    config.parameters = generateMockBasicValues('G11', DEFAULT_MONTHLY_CONSUMPTION, 'REGULATED');
    return config;
  });

  return {
    formValues: {
      calculationYear: DEFAULT_CALCULATION_YEAR,
      distributor: DEFAULT_DISTRIBUTOR,
      handlingFeeType: DEFAULT_HANDLING_FEE_TYPE as HandlingFeeType,
      tariffCategory: 'HOUSEHOLD', // Default to household tariffs
      months: months as ProCalculatorFormValues['months'],
    },
    results: null,
    loading: false,
    generatingParams: false,
    importingData: false,
    error: null,
    baselineTariff: DEFAULT_BASELINE_TARIFF as TariffType,
    hourlyPatternFromCSV: undefined,
    hasHourlyData: null,
    hasG14PriceData: false,
    includeG14: true,
    isSaving: false,
    saveError: undefined,
    saveSuccess: false,
    calculationExists: false,
  };
}

/**
 * State reducer
 * Handles all state transitions
 */
function proCalculatorReducer(
  state: ProCalculatorState,
  action: ProCalculatorAction
): ProCalculatorState {
  switch (action.type) {
    case 'SET_FORM_VALUES':
      return { ...state, formValues: action.payload };

    case 'SET_RESULTS':
      return { ...state, results: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_GENERATING_PARAMS':
      return { ...state, generatingParams: action.payload };

    case 'SET_IMPORTING_DATA':
      return { ...state, importingData: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_BASELINE_TARIFF':
      return { ...state, baselineTariff: action.payload };

    case 'SET_HOURLY_PATTERN_FROM_CSV':
      return { ...state, hourlyPatternFromCSV: action.payload };

    case 'SET_HAS_HOURLY_DATA':
      return { ...state, hasHourlyData: action.payload };

    case 'SET_HAS_G14_PRICE_DATA':
      return { ...state, hasG14PriceData: action.payload };

    case 'SET_INCLUDE_G14':
      return { ...state, includeG14: action.payload };

    case 'SET_IS_SAVING':
      return { ...state, isSaving: action.payload };

    case 'SET_SAVE_ERROR':
      return { ...state, saveError: action.payload };

    case 'SET_SAVE_SUCCESS':
      return { ...state, saveSuccess: action.payload };

    case 'SET_CALCULATION_EXISTS':
      return { ...state, calculationExists: action.payload };

    case 'UPDATE_MONTH_CONFIG': {
      const updatedMonths = [...state.formValues.months];
      updatedMonths[action.month] = {
        ...updatedMonths[action.month],
        ...action.config,
      };
      return {
        ...state,
        formValues: {
          ...state.formValues,
          months: updatedMonths as ProCalculatorFormValues['months'],
        },
      };
    }

    default:
      return state;
  }
}

/**
 * useProCalculatorState Hook
 *
 * Manages all calculator state with useReducer pattern
 *
 * @returns Tuple of [state, dispatch]
 */
export function useProCalculatorState() {
  return useReducer(proCalculatorReducer, undefined, createInitialState);
}
